// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { Stack, useRouter } from 'expo-router';
import { Linking, Pressable, ScrollView, StyleSheet } from 'react-native';

import { Text, View } from '@/components/Themed';
import { useUser } from '@/app/user-context';
import { useEffectiveRoles } from '@/hooks/useEffectiveRoles';
import { useI18n } from '@/i18n';
import appColors from '@/theme/colors';
import { DECKS, DECK_SLUGS } from '@/features/documentation/decks';

type Translate = (key: string, params?: Record<string, string | number>) => string;

/** The repository's main branch on Gitea, where every markdown document is rendered. */
const REPO_BASE_URL = 'https://git.zyberze.ro/goffy/goffy/src/branch/main';

type DocLink = {
  /** `documentationPage.docs.<key>` carries the title. */
  key: string;
  href: string;
};

const REPOSITORY_DOCS: readonly DocLink[] = [
  { key: 'architectureRules', href: `${REPO_BASE_URL}/CLAUDE.md` },
  { key: 'courseAnalysisService', href: `${REPO_BASE_URL}/services/course-analysis-service/README.md` },
  { key: 'trainingTools', href: `${REPO_BASE_URL}/services/course-analysis-service/training/README.md` },
  { key: 'baseline', href: `${REPO_BASE_URL}/services/course-analysis-service/training/BASELINE.md` },
  { key: 'batchWorkers', href: `${REPO_BASE_URL}/docs/batch-workers.md` },
  { key: 'addingGolfGame', href: `${REPO_BASE_URL}/docs/adding-a-golf-game.md` },
  { key: 'gitWorkflow', href: `${REPO_BASE_URL}/docs/git-workflow.md` },
  { key: 'keycloakProd', href: `${REPO_BASE_URL}/docs/keycloak-prod-access.md` },
  { key: 'postgresBackup', href: `${REPO_BASE_URL}/docs/postgres-backup-restore.md` },
  { key: 'swish', href: `${REPO_BASE_URL}/docs/swish-recurring-payments.md` },
  { key: 'envoy401', href: `${REPO_BASE_URL}/docs/envoy-401-debug.md` },
  { key: 'tourCreatorRepair', href: `${REPO_BASE_URL}/docs/tour-creator-membership-repair.md` },
  { key: 'asyncApi', href: `${REPO_BASE_URL}/docs/asyncapi-html/asyncapi-rabbitmq-docs` },
];

/**
 * Where the documentation is: the presentations shipped inside the app, and the documents kept
 * beside the code in the repository. Developer-or-admin gated like the other operational pages
 * behind the cog menu — nothing here is secret, but none of it is for players either.
 */
export default function DocumentationScreen() {
  const { t } = useI18n();
  const router = useRouter();
  const { user } = useUser();
  const { isDeveloper, isAdmin } = useEffectiveRoles(user);
  const canView = isDeveloper || isAdmin;

  return (
    <>
      <Stack.Screen options={{ title: t('documentationPage.title') }} />
      <ScrollView contentContainerStyle={styles.container}>
        <Text style={styles.title}>{t('documentationPage.title')}</Text>
        <Text style={styles.intro}>{t('documentationPage.intro')}</Text>

        {!canView ? (
          <Text style={styles.paragraph} testID="documentation-forbidden">
            {t('documentationPage.forbidden')}
          </Text>
        ) : (
          <>
            <Text style={styles.heading}>{t('documentationPage.presentationsHeading')}</Text>
            <Text style={styles.detail}>{t('documentationPage.presentationsDetail')}</Text>
            {DECK_SLUGS.map((slug) => (
              <DocCard
                key={slug}
                testID={`documentation-${DECKS[slug].i18nKey}`}
                title={t(`documentationPage.docs.${DECKS[slug].i18nKey}.title`)}
                description={t(`documentationPage.docs.${DECKS[slug].i18nKey}.description`)}
                hint={t('documentationPage.opensHere')}
                onPress={() => router.push(`/documentation/${slug}`)}
              />
            ))}

            <Text style={styles.heading}>{t('documentationPage.repositoryHeading')}</Text>
            <Text style={styles.detail}>{t('documentationPage.repositoryDetail')}</Text>
            {REPOSITORY_DOCS.map((doc) => (
              <DocCard
                key={doc.key}
                testID={`documentation-${doc.key}`}
                title={t(`documentationPage.docs.${doc.key}`)}
                hint={pathOf(doc.href, t)}
                onPress={() => {
                  void Linking.openURL(doc.href);
                }}
              />
            ))}
          </>
        )}
      </ScrollView>
    </>
  );
}

/** The path inside the repository, which is what tells two READMEs apart. */
const pathOf = (href: string, t: Translate): string =>
  href.startsWith(REPO_BASE_URL)
    ? href.slice(REPO_BASE_URL.length + 1)
    : t('documentationPage.opensInBrowser');

function DocCard({
  title,
  description,
  hint,
  onPress,
  testID,
}: {
  title: string;
  description?: string;
  hint: string;
  onPress: () => void;
  testID: string;
}) {
  return (
    <Pressable style={styles.card} accessibilityRole="link" testID={testID} onPress={onPress}>
      <Text style={styles.cardTitle}>{title}</Text>
      {description ? <Text style={styles.cardDescription}>{description}</Text> : null}
      <Text style={styles.cardHint}>{hint}</Text>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  container: { padding: 16, gap: 4 },
  title: { fontSize: 26, fontWeight: '700', color: appColors.text.primary },
  intro: { color: appColors.text.secondary, fontSize: 13, marginBottom: 12, lineHeight: 18 },
  paragraph: { color: appColors.text.secondary, marginBottom: 12 },
  heading: { fontSize: 17, fontWeight: '600', color: appColors.text.primary, marginTop: 12 },
  detail: { color: appColors.text.secondary, fontSize: 13, marginBottom: 8, lineHeight: 18 },
  card: {
    borderRadius: 12,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: appColors.border.default,
    padding: 12,
    marginBottom: 8,
    gap: 4,
  },
  cardTitle: { color: appColors.text.primary, fontWeight: '600', fontSize: 15 },
  cardDescription: { color: appColors.text.secondary, fontSize: 13, lineHeight: 18 },
  cardHint: { color: appColors.text.muted, fontSize: 12 },
});
